import { Button, InstallSnippet, LinkButton } from '@mdxui/primitives'
import { motion, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { cn } from '../../lib/cn'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import {
  gridContainerVariants,
  gridItemVariants,
  heroButtonsVariants,
  heroDescriptionVariants,
  heroHeadlineVariants,
  reducedMotionVariants,
  sectionVariants,
} from '../../lib/motion'
import { CodeWindowBase } from '../code-window'
import type { HeroBentoProps, CardContent } from './types'

const defaultCodeMain = `import { Hero } from '@mdxui/neo'

export default function LandingPage() {
  return (
    <Hero
      variant="code-bento"
      title="Build Beautiful Interfaces"
      subtitle="Type-safe components for MDX"
      callToAction="Get Started"
      callToActionUrl="/docs"
    />
  )
}`

const defaultCodeLeft = `// Install with your package manager
pnpm add mdxui @mdxui/neo

// Or with npm
npm install mdxui @mdxui/neo`

const defaultCodeRight = `import { Features, Pricing, CTA } from '@mdxui/neo'

// Compose your landing page
<Features variant="grid" features={features} />
<Pricing variant="cards" plans={plans} />
<CTA heading="Ready to start?" />`

const defaultMainCard: CardContent = {
  label: 'PRODUCTION READY',
  title: 'Ready to use blocks for your project',
  description:
    'Type-safe components for building Sites and Apps with MDX. Fully accessible and production-ready.',
  link: { text: 'Learn more', url: '#' },
}

const defaultLeftCard: CardContent = {
  label: 'EASY TO USE',
  title: 'Customize and build your project',
}

const defaultRightCard: CardContent = {
  label: 'A BLOCK FOR EVERYTHING',
  title: 'Neo for every project',
  link: { text: 'Learn more', url: '#' },
}

/**
 * HeroBento - Centered hero with bento grid of code windows and card content
 *
 * **Layout:**
 * ```
 * +------------------------------------+
 * |           Heading                  |
 * |         Description                |
 * |     [Primary] [Secondary]          |
 * +-----------------+------------------+
 * |   Main Card     |   Code Window    |
 * |   Content       |   (borderless)   |
 * +--------+--------+------------------+
 * |  Code  |   Right Card   |  Code    |
 * |  Left  |   Content      |  Right   |
 * +--------+----------------+----------+
 * ```
 *
 * **Best Use Cases:**
 * - Developer documentation landing pages
 * - SDK/API getting started sections
 * - Code-first product showcases
 */
const HeroBento = ({
  title = 'Build Beautiful Interfaces with MDXUI',
  subtitle = 'Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.',
  callToAction,
  callToActionUrl,
  secondaryCallToAction,
  secondaryCallToActionUrl,
  installSnippet,
  mainCard = defaultMainCard,
  codeWindowMain = {
    code: defaultCodeMain,
    language: 'tsx',
    filename: 'page.tsx',
  },
  leftCard = defaultLeftCard,
  codeWindowLeft = {
    code: defaultCodeLeft,
    language: 'bash',
    filename: 'terminal',
  },
  rightCard = defaultRightCard,
  codeWindowRight = {
    code: defaultCodeRight,
    language: 'tsx',
    filename: 'sections.tsx',
  },
  codeTheme = 'dark',
  background,
  behindNavbar = true,
  className,
}: Omit<HeroBentoProps, 'variant'>) => {
  const prefersReducedMotion = useReducedMotion()

  const headlineAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroHeadlineVariants
  const descAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : heroButtonsVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : gridItemVariants

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        behindNavbar && BEHIND_NAVBAR_CLASSES,
        className,
      )}
    >
      {background && <div className="absolute inset-0">{background}</div>}
      <motion.section
        className="relative z-10 py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container mx-auto">
          {/* Header section */}
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-6">
            <motion.h1
              variants={headlineAnimation}
              className="mb-2 text-center text-4xl font-semibold text-balance lg:text-5xl"
            >
              {title}
            </motion.h1>
            {subtitle && (
              <motion.div
                variants={descAnimation}
                className="text-center text-muted-foreground lg:text-lg [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-base [&_code]:text-foreground"
              >
                <Markdown>{subtitle}</Markdown>
              </motion.div>
            )}
            {(callToAction || secondaryCallToAction || installSnippet) && (
              <motion.div
                variants={buttonsAnimation}
                className="mt-6 flex w-full flex-col items-center justify-center gap-2 sm:flex-row"
              >
                {installSnippet ? (
                  <InstallSnippet
                    package={installSnippet.package}
                    command={installSnippet.command}
                    className="w-full sm:w-auto"
                  />
                ) : (
                  <>
                    {callToAction && callToActionUrl && (
                      <Button asChild className="w-full sm:w-auto">
                        <a href={callToActionUrl}>{callToAction}</a>
                      </Button>
                    )}
                    {secondaryCallToAction && secondaryCallToActionUrl && (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full sm:w-auto"
                      >
                        <a href={secondaryCallToActionUrl}>
                          {secondaryCallToAction}
                        </a>
                      </Button>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </div>

          {/* Bento grid */}
          <motion.div
            className="mx-auto mt-20 flex max-w-6xl grid-cols-1 flex-col gap-6 lg:grid lg:grid-cols-7"
            variants={gridContainerVariants(0.1, 0.3)}
          >
            {/* Main card - full width, content left, code right */}
            <motion.div
              variants={itemAnimation}
              className="col-span-7 grid overflow-hidden rounded-[var(--radius)] border border-border bg-card sm:grid-cols-2 shadow-sm"
            >
              <div className="flex min-w-0 flex-col justify-between p-6 lg:p-8">
                <div className="min-w-0">
                  <div className="mb-4 text-xs text-muted-foreground">
                    {mainCard.label}
                  </div>
                  <h3 className="mb-2 break-words text-xl font-semibold lg:text-2xl">
                    {mainCard.title}
                  </h3>
                  {mainCard.description && (
                    <p className="break-words text-sm text-muted-foreground">
                      {mainCard.description}
                    </p>
                  )}
                </div>
                {mainCard.link && (
                  <div className="mt-6 sm:mt-8">
                    <LinkButton href={mainCard.link.url}>
                      {mainCard.link.text}
                    </LinkButton>
                  </div>
                )}
              </div>
              <div className="order-first h-full min-w-0 sm:order-last p-px">
                <CodeWindowBase
                  code={codeWindowMain.code}
                  language={codeWindowMain.language}
                  theme={codeTheme}
                  style="borderless"
                  showHeader={false}
                  showCopy={false}
                  className="h-full rounded-[var(--radius-md,calc(var(--radius)*0.75))]"
                  contentClassName="h-full sm:min-h-[350px]"
                />
              </div>
            </motion.div>

            {/* Left card - smaller, code on top */}
            <motion.div
              variants={itemAnimation}
              className="group relative overflow-hidden rounded-[var(--radius)] border border-border bg-card lg:col-span-3 shadow-sm"
            >
              <div className="shrink-0">
                <CodeWindowBase
                  code={codeWindowLeft.code}
                  language={codeWindowLeft.language}
                  theme={codeTheme}
                  style="borderless"
                  showHeader={false}
                  showCopy={false}
                  contentClassName="sm:min-h-[200px] sm:max-h-[200px]"
                />
              </div>
              <div className="p-6 lg:p-8">
                <div className="mb-4 text-xs text-muted-foreground">
                  {leftCard.label}
                </div>
                <h3 className="mb-2 text-xl font-semibold lg:text-2xl">
                  {leftCard.title}
                </h3>
                {leftCard.description && (
                  <p className="text-sm text-muted-foreground">
                    {leftCard.description}
                  </p>
                )}
                {leftCard.link && (
                  <div className="mt-4">
                    <LinkButton href={leftCard.link.url}>
                      {leftCard.link.text}
                    </LinkButton>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Right card - wider, code on top, content below */}
            <motion.div
              variants={itemAnimation}
              className="overflow-hidden rounded-[var(--radius)] border border-border bg-card lg:col-span-4 lg:flex lg:flex-col shadow-sm"
            >
              <CodeWindowBase
                code={codeWindowRight.code}
                language={codeWindowRight.language}
                theme={codeTheme}
                style="borderless"
                showHeader={false}
                showCopy={false}
                contentClassName="h-full sm:min-h-[200px] sm:max-h-[200px]"
              />
              <div className="p-6 lg:flex-1 lg:p-8">
                <div className="mb-4 text-xs text-muted-foreground">
                  {rightCard.label}
                </div>
                <h3 className="mb-2 text-xl font-semibold lg:text-2xl">
                  {rightCard.title}
                </h3>
                {rightCard.description && (
                  <p className="text-sm text-muted-foreground">
                    {rightCard.description}
                  </p>
                )}
                {rightCard.link && (
                  <div className="mt-4">
                    <LinkButton href={rightCard.link.url}>
                      {rightCard.link.text}
                    </LinkButton>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  )
}

export { HeroBento }
